<template>
  <ValidationProvider :vid="vid" :name="$attrs.name" :rules="rules">
    <b-form-group slot-scope="{ valid, errors }" v-bind="$attrs">
      <template #label>
        <label>
          {{ $attrs.label }}
          <span v-if="$attrs.required || rules.includes('required')" class="text-danger font-bold"
            >*</span
          >
        </label>
      </template>
      <v-select
        v-model="selectedSalesType"
        :options="salesTypeOptions"
        :filterable="false"
        placeholder="Select SalesType"
        :loading="loadingSalesTypes || loadingSelectedSalesType"
        :clearable="true"
        :class="!!errors[0] ? 'is-invalid' : ''"
        @search="onSearch"
        :disabled="$attrs.disabled"
        :state="errors[0] ? false : rules && rules.includes('required') && valid ? true : null"
        v-bind="$attrs"
      >
        <template #no-options>
          <div class="text-center text-muted">No SalesTypes found</div>
        </template>

        <template #spinner="{ loading }">
          <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </template>

        <template #selected-option="{ id, code, name, prefix }">
          <span>
            <small class="text-muted mr-1">{{ id }}</small>
            ({{ prefix }})
            <strong class="mr-1">{{ name }}</strong>
            {{ code }}
          </span>
        </template>

        <template #option="{ id, name, code, prefix }">
          <span>
            <small class="text-muted mr-1">{{ id }}</small>
            ({{ prefix }})
            <strong class="mr-1">{{ name }}</strong>
            {{ code }}
          </span>
        </template>
      </v-select>

      <b-form-invalid-feedback id="inputLiveFeedback">
        {{ errors[0] ? errors[0].replace('{field}', $attrs.label) : '' }}
      </b-form-invalid-feedback>
    </b-form-group>
  </ValidationProvider>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { BCol, BRow } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import SalesTypeStoreModule from '../../sales-type/SalesTypeStoreModule';

export default {
  name: 'SalesTypeDropdownField',
  components: {
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormInvalidFeedback,
  },
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'SalesType',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
  },
  setup(props, { emit }) {
    const SALES_TYPE_STORE_MODULE_NAME = 'sales-type';

    // Register module
    if (!store.hasModule(SALES_TYPE_STORE_MODULE_NAME))
      store.registerModule(SALES_TYPE_STORE_MODULE_NAME, SalesTypeStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SALES_TYPE_STORE_MODULE_NAME))
        store.unregisterModule(SALES_TYPE_STORE_MODULE_NAME);
    });

    // Reactive references
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingSalesTypes = ref(false);
    const loadingSelectedSalesType = ref(false);
    const salesTypeOptions = ref([]);
    const selectedSalesType = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);
    // Methods
    const fetchSalesTypes = async () => {
      loadingSalesTypes.value = true;
      try {
        const response = await api.get(`/SalesTypes/GetByPagination/${businessID.value}`, {
          params: {
            searchTerm: searchTerm.value,
            pageNumber: pageNumber.value,
            pageSize: 999,
          },
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          salesTypeOptions.value = [...response.data.data.items];
        } else {
          console.error(
            '=== SalesTypeDropdownField options Error in paginated sales type data format:',
            response.data
          );
          salesTypeOptions.value = [];
        }
      } catch (error) {
        console.error('=== SalesTypeDropdownField options Error fetching sales types:', error);
      } finally {
        loadingSalesTypes.value = false;
      }
    };

    const onSearch = (searchTermValue) => {
      // Debounce search
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTerm.value = searchTermValue;

      if (!searchTermValue || searchTermValue.trim() === '') {
        searchTerm.value = '';
        fetchSalesTypes();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        fetchSalesTypes();
      }, 300);
    };

    // Watchers
    watch(
      selectedSalesType,
      (newVal) => {
        emit('input', newVal?.id);
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      const salesTypeId = props.value;

      fetchSalesTypes();

      if (salesTypeId) {
        loadingSelectedSalesType.value = true;
        store
          .dispatch('sales-type/fetchSalesType', { id: salesTypeId })
          .then((salesType) => {
            selectedSalesType.value = salesType;
          })
          .catch((error) => {
            console.log('error:', error);
          })
          .finally(() => {
            loadingSelectedSalesType.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      searchTerm,
      pageNumber,
      loadingSalesTypes,
      loadingSelectedSalesType,
      salesTypeOptions,
      selectedSalesType,
      searchTimeout,

      // Computed properties
      businessID,

      // Methods
      fetchSalesTypes,
      onSearch,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
